import { useSuspenseQuery } from "@tanstack/react-query";
import { createMiddleware, createServerOnlyFn } from "@tanstack/react-start";
import { setResponseStatus } from "@tanstack/react-start/server";

import { useTRPC } from "@/integrations/trpc/react";
import { forbidden } from "@/lib/responses";

import { authClient } from "./client";
import type { Permissions, Role } from "./permissions";
import { getViewerResult, type Viewer } from "./server";

export function isAdminRole(role: string | null | undefined): boolean {
  return role === "admin" || role === "superadmin";
}

export function isSuperadminRole(role: string | null | undefined): boolean {
  return role === "superadmin";
}

export type SessionViewer = Pick<
  Viewer,
  | "id"
  | "name"
  | "role"
  | "username"
  | "email"
  | "emailVerified"
  | "phoneNumber"
  | "phoneNumberVerified"
  | "needsPasswordChange"
>;

export type ViewerParam = { id: string; role: string | null };

export function toSessionViewer(viewer: Viewer): SessionViewer | undefined {
  if (!viewer?.id) return undefined;
  return {
    id: viewer.id,
    name: viewer.name,
    role: viewer.role,
    username: viewer.username,
    email: viewer.email,
    emailVerified: viewer.emailVerified,
    phoneNumber: viewer.phoneNumber,
    phoneNumberVerified: viewer.phoneNumberVerified,
    needsPasswordChange: viewer.needsPasswordChange,
  };
}

export const authMiddleware = createMiddleware().server(async ({ next }) => {
  const { viewer, impersonatorId, sessionError } = await getViewerResult();

  return await next({
    context: {
      viewer: toSessionViewer(viewer),
      impersonatorId,
      sessionError,
    },
  });
});
// .client(async ({ next }) => {
// // authClient.
// 	const result = await next(); // <-- This will execute the next middleware in the chain and eventually, the RPC to the server
// 	return result;
// });

/**
 * Stale time for viewer queries during navigation (beforeLoad).
 * Keeps viewer data reasonably fresh across page transitions.
 */
export const VIEWER_NAVIGATION_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Shared viewer query hook. Uses staleTime: Infinity so that component
 * hooks never trigger background refetches — this prevents transient
 * server failures (or dev HMR) from overwriting a valid cached viewer
 * with null. The viewer is refreshed on navigation via beforeLoad's
 * ensureQueryData, and on explicit invalidation (login, impersonation,
 * profile update, etc.).
 */
function useViewerQuery() {
  const trpc = useTRPC();
  return useSuspenseQuery({
    ...trpc.viewer.get.queryOptions(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useViewerId() {
  const { data: viewer } = useViewerQuery();
  return viewer?.id ?? null;
}

export function useIsLoggedIn() {
  const { data: viewer } = useViewerQuery();
  return typeof viewer?.id === "string";
}

export function useViewer() {
  const { data: viewer } = useViewerQuery();
  return viewer;
}

export function useViewerRole() {
  const { data: viewer } = useViewerQuery();
  return viewer?.role;
}

export function useViewerIsAdmin() {
  const { data: viewer } = useViewerQuery();
  return viewer ? isAdminRole(viewer.role) : undefined;
}

export function useViewerHasPermission<P extends Permissions>(permissions: P) {
  const viewer = useViewer();

  if (!viewer) {
    return false;
  }

  return roleHasPermission(viewer.role, permissions);
}

export function useImpersonator() {
  const trpc = useTRPC();
  const { data: impersonator } = useSuspenseQuery(
    trpc.viewer.getImpersonator.queryOptions(),
  );

  return impersonator;
}

export function useImpersonatorRole() {
  const trpc = useTRPC();
  const { data: impersonator } = useSuspenseQuery(
    trpc.viewer.getImpersonator.queryOptions(),
  );

  return impersonator?.role;
}

export function useImpersonatorIsAdmin() {
  const trpc = useTRPC();
  const { data: impersonator } = useSuspenseQuery(
    trpc.viewer.getImpersonator.queryOptions(),
  );

  return impersonator ? isAdminRole(impersonator.role) : false;
}

export function useImpersonatorHasPermission<P extends Permissions>(
  permissions: P,
) {
  const impersonator = useImpersonator();

  if (!impersonator?.role) {
    return false;
  }

  return roleHasPermission(impersonator.role, permissions);
}

export function roleHasPermission<P extends Permissions>(
  role: Role,
  permissions: P,
) {
  return authClient.admin.checkRolePermission({
    permissions,
    role,
  });
}

export const requireAuthenticated = createMiddleware()
  .middleware([authMiddleware])
  .server(async ({ next, context }) => {
    const { viewer } = context;

    if (!viewer) {
      setResponseStatus(401);

      throw new Error("Unauthorized");
    }

    // Type assertion is safe here because we've checked viewer exists above
    return await next({
      context: {
        ...context,
        viewer: viewer as SessionViewer,
      },
    });
  });

/**
 * Creates a middleware that ensures the logged-in user has the specified permissions.
 *
 * @param permissions - The permissions to check (e.g., { tournaments: ["create", "update"] })
 * @param options - Optional configuration
 * @param options.requireAuth - If true, requires user to be authenticated (default: true)
 * @param options.onUnauthorized - Custom error message or handler (default: "Unauthorized")
 *
 * @example
 * ```typescript
 * export const createTournamentFn = createServerFn({ method: "POST" })
 *   .middleware([
 *     authMiddleware,
 *     requirePermissions({ tournaments: ["create"] })
 *   ])
 *   .handler(async ({ context, data }) => {
 *     // User is guaranteed to have tournament:create permission
 *     // context.viewer is typed and available
 *   })
 * ```
 *
 * @example
 * ```typescript
 * // Require multiple permissions
 * export const updateVenueFn = createServerFn({ method: "POST" })
 *   .middleware([
 *     authMiddleware,
 *     requirePermissions({ venues: ["update"] })
 *   ])
 *   .handler(async ({ context }) => {
 *     // User has venues:update permission
 *   })
 * ```
 *
 * @example
 * ```typescript
 * // Custom error message
 * export const deleteContentFn = createServerFn({ method: "POST" })
 *   .middleware([
 *     authMiddleware,
 *     requirePermissions(
 *       { content: ["delete"] },
 *       { onUnauthorized: "You do not have permission to delete content" }
 *     )
 *   ])
 *   .handler(async ({ context }) => {
 *     // User has content:delete permission
 *   })
 * ```
 */
export function requirePermissions<P extends Permissions>(permissions: P) {
  return createMiddleware()
    .middleware([authMiddleware])
    .server(async ({ next, context }) => {
      const { viewer } = context;

      if (!viewer) {
        setResponseStatus(401);

        throw new Error("Unauthorized");
      }

      const hasPermission = roleHasPermission(viewer.role, permissions);

      if (!hasPermission) {
        setResponseStatus(403);

        throw new Error("Forbidden");
      }

      // Type assertion is safe here because we've checked viewer exists above
      return await next({
        context: {
          ...context,
          viewer: viewer as SessionViewer,
        },
      });
    });
}

export const assertRoleHasPermission = createServerOnlyFn(
  <P extends Permissions>(role: Role, permissions: P) => {
    if (!roleHasPermission(role, permissions)) {
      throw forbidden();
    }
  },
);

/**
 * Creates a middleware that ensures the logged-in user has one of the specified roles.
 *
 * @param roles - Array of allowed roles (e.g., ["admin", "td"])
 * @param options - Optional configuration
 * @param options.requireAuth - If true, requires user to be authenticated (default: true)
 * @param options.onUnauthorized - Custom error message (default: "Unauthorized")
 *
 * @example
 * ```typescript
 * export const adminOnlyFn = createServerFn({ method: "POST" })
 *   .middleware([
 *     authMiddleware,
 *     requireRole(["admin"])
 *   ])
 *   .handler(async ({ context }) => {
 *     // User is guaranteed to be an admin
 *   })
 * ```
 *
 * @example
 * ```typescript
 * // Allow multiple roles
 * export const tournamentManagementFn = createServerFn({ method: "POST" })
 *   .middleware([
 *     authMiddleware,
 *     requireRole(["admin", "td"])
 *   ])
 *   .handler(async ({ context }) => {
 *     // User is either admin or td
 *   })
 * ```
 */
export function requireRole(roles: Role[]) {
  return createMiddleware()
    .middleware([authMiddleware])
    .server(async ({ next, context }) => {
      const { viewer } = context;

      if (!viewer) {
        setResponseStatus(401);

        throw new Error("Unauthorized");
      }

      if (!roles.includes(viewer.role)) {
        setResponseStatus(403);

        throw new Error(
          `Forbidden: requires one of the following roles: ${roles.join(", ")}`,
        );
      }

      // Type assertion is safe here because we've checked viewer exists above
      return await next({
        context: {
          ...context,
          viewer: viewer as SessionViewer,
        },
      });
    });
}
